import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Grid, Chip, Button, CircularProgress, Alert, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, Stack, Snackbar,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useEmployeeAuth } from '../hooks/useEmployeeAuth';
import { employeePortalAPI } from '../utils/api';
import { formatDateOnly, formatCalendarTimeRange, getTodayDateInCompanyTimezone } from '../utils/timezone';
import ConfirmDialog from './ConfirmDialog';
import type { EmployeeScheduleData, ScheduleShift, ShiftSwapRequest, SwapStatus, ShiftSwapCandidate } from '../types/shift';

const pad = (n: number) => String(n).padStart(2, '0');
const toISO = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const addDays = (iso: string, n: number) => {
  const d = new Date(iso + 'T00:00:00');
  d.setDate(d.getDate() + n);
  return toISO(d);
};

const STATUS_VARIANT: Record<SwapStatus, 'default' | 'success' | 'error' | 'warning' | 'info'> = {
  pending: 'warning',
  accepted: 'info',
  approved: 'success',
  declined: 'error',
  rejected: 'error',
  cancelled: 'default',
};

interface EmployeeShiftPanelProps {
  timezone?: string | null;
}

const EmployeeShiftPanel: React.FC<EmployeeShiftPanelProps> = ({ timezone }) => {
  const { t, i18n } = useTranslation('auth');
  const { t: tc } = useTranslation('common');
  const { t: tCalendars } = useTranslation('pages', { keyPrefix: 'calendars' });
  const { employee } = useEmployeeAuth();

  const companyTimezone = timezone || null;

  const [schedule, setSchedule] = useState<EmployeeScheduleData | null>(null);
  const [swaps, setSwaps] = useState<ShiftSwapRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Swap request modal
  const [swapOpen, setSwapOpen] = useState(false);
  const [swapDate, setSwapDate] = useState('');
  const [partnerId, setPartnerId] = useState<number | ''>('');
  const [reason, setReason] = useState('');
  const [swapError, setSwapError] = useState<string | null>(null);
  const [swapCandidates, setSwapCandidates] = useState<ShiftSwapCandidate[]>([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [confirmCancelSwap, setConfirmCancelSwap] = useState<number | null>(null);

  const todayISO = useMemo(() => companyTimezone ? getTodayDateInCompanyTimezone(companyTimezone) : toISO(new Date()), [companyTimezone]);
  const weekDates = useMemo(() => {
    const start = new Date(todayISO + 'T00:00:00');
    start.setDate(start.getDate() - ((start.getDay() + 6) % 7));
    const from = toISO(start);
    const to = addDays(from, 6);
    return { from, to, scheduleTo: addDays(from, 34), list: (() => { const l: string[] = []; let c = from; while (c <= to) { l.push(c); c = addDays(c, 1); } return l; })() };
  }, [todayISO]);

  const fetchData = useCallback(async () => {
    if (!employee) return;
    setLoading(true);
    setError(null);
    try {
      const [sched, sw] = await Promise.all([
        employeePortalAPI.getMySchedule({ date_from: weekDates.from, date_to: weekDates.scheduleTo }),
        employeePortalAPI.getMySwaps(employee.company_id),
      ]);
      setSchedule(sched.data || { date_from: '', date_to: '', shifts: [] });
      setSwaps(sw.data || []);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || t('employeePortal.fetchDataError'));
    } finally {
      setLoading(false);
    }
  }, [employee, weekDates.from, weekDates.scheduleTo, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const myCellForDate = useCallback(
    (date: string) => schedule?.shifts.find((s) => s.date === date)?.shift || null,
    [schedule]
  );

  const todayCell = myCellForDate(todayISO);

  // Outgoing = I am requester; Incoming = I am target
  const outgoing = useMemo(() => swaps.filter((s) => s.requester_employee_id === employee?.id), [swaps, employee]);
  const incoming = useMemo(() => swaps.filter((s) => s.target_employee_id === employee?.id), [swaps, employee]);

  const swapDates = useMemo(() => {
    if (!schedule) return [];
    return schedule.shifts
      .filter((s) => s.date >= todayISO && !!s.shift)
      .map((s) => s.date);
  }, [schedule, todayISO]);

  useEffect(() => {
    if (!swapDate) {
      setSwapCandidates([]);
      return;
    }
    let cancelled = false;
    setCandidatesLoading(true);
    setSwapError(null);
    employeePortalAPI.getSwapCandidates(swapDate)
      .then((response) => {
        if (!cancelled) setSwapCandidates(response.data || []);
      })
      .catch((err: any) => {
        if (!cancelled) {
          setSwapCandidates([]);
          setSwapError(err.response?.data?.message || t('employeeDashboard.swapCandidatesFailed'));
        }
      })
      .finally(() => {
        if (!cancelled) setCandidatesLoading(false);
      });
    return () => { cancelled = true; };
  }, [swapDate, t]);

  const openSwapModal = () => {
    setSwapDate('');
    setPartnerId('');
    setReason('');
    setSwapError(null);
    setSwapCandidates([]);
    setSwapOpen(true);
  };

  const handleCreateSwap = async () => {
    if (!employee) return;
    if (!swapDate || !partnerId) {
      setSwapError(t('employeeDashboard.selectPartner'));
      return;
    }
    setActionLoading(true);
    setSwapError(null);
    try {
      await employeePortalAPI.createSwapRequest(employee.company_id, {
        target_employee_id: Number(partnerId),
        swap_date: swapDate,
        requester_reason: reason || undefined,
      });
      const target = swapCandidates.find((candidate) => candidate.id === Number(partnerId));
      setSuccessMessage(t('employeeDashboard.swapSent', { target: target?.name || '' }));
      setSwapOpen(false);
      fetchData();
    } catch (err: any) {
      setSwapError(err.response?.data?.message || err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRespond = async (swapId: number, action: 'accept' | 'decline') => {
    if (!employee) return;
    setActionLoading(true);
    try {
      await employeePortalAPI.respondToSwap(employee.company_id, swapId, {
        response: action === 'accept' ? 'accepted' : 'declined',
      });
      setSuccessMessage(t(action === 'accept' ? 'employeeDashboard.swapAccepted' : 'employeeDashboard.swapDeclined'));
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async (swapId: number) => {
    if (!employee) return;
    setActionLoading(true);
    try {
      await employeePortalAPI.cancelSwap(employee.company_id, swapId);
      setSuccessMessage(t('employeeDashboard.swapCancelled'));
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress sx={{ color: 'var(--an-primary)' }} />
      </Box>
    );
  }

  const renderShiftChip = (tpl: ScheduleShift | null | undefined, size: 'small' | 'medium' = 'small') => (
    <Chip
      label={tpl ? (tpl.code || tpl.name?.slice(0, 6)) : '—'}
      size={size}
      sx={{
        backgroundColor: tpl?.color || 'var(--an-primary)',
        color: 'var(--an-white)',
        fontWeight: 800,
      }}
    />
  );

  const formatSwapDate = (value: string, short = false) => {
    try {
      const locale = i18n.language || 'en';
      return short
        ? formatDateOnly(value, locale, { weekday: 'short', month: 'short', day: 'numeric' })
        : formatDateOnly(value, locale, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return value.slice(0, 10);
    }
  };

  return (
    <Box sx={{ mt: 4 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 'var(--an-radius-md)' }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* My Schedule */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Box sx={{
            borderRadius: 'var(--an-radius-md)',
            border: '1px solid var(--an-border-light)',
            boxShadow: 'var(--an-shadow-sm)',
            backgroundColor: 'var(--an-surface)',
            p: 3,
            height: '100%',
          }}>
            <Typography variant="h6" fontWeight={800} sx={{ mb: 2, color: 'var(--an-text-primary)' }}>
              <i className="bi bi-calendar2-week me-2" />
              {t('employeeDashboard.mySchedule')}
            </Typography>

            {/* Today's shift */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ color: 'var(--an-text-secondary)', fontWeight: 700 }}>
                {t('employeeDashboard.todayShift')}
              </Typography>
              {todayCell ? (
                <Box sx={{ mt: 1 }}>
                  <Stack direction='row' spacing={1} alignItems='center'>
                    {renderShiftChip(todayCell, 'medium')}
                    <Typography fontWeight={700} sx={{ color: 'var(--an-text-primary)' }}>
                      {todayCell.name}
                    </Typography>
                  </Stack>
                  {todayCell.calendar && (
                    <Typography variant='body2' sx={{ color: 'var(--an-text-secondary)', mt: 1 }}>
                      {formatCalendarTimeRange(todayCell.calendar.check_in_time || '', todayCell.calendar.check_out_time || '', todayCell.calendar.spans_next_day, companyTimezone || undefined, tCalendars('nextDay'))}
                    </Typography>
                  )}
                </Box>
              ) : (
                <Typography variant="body2" sx={{ color: 'var(--an-text-secondary)', mt: 1 }}>
                  {t('employeeDashboard.noShiftToday')}
                </Typography>
              )}
            </Box>

            {/* This week */}
            <Box>
              <Typography variant="subtitle2" sx={{ color: 'var(--an-text-secondary)', fontWeight: 700, mb: 1 }}>
                {t('employeeDashboard.thisWeek')}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ overflowX: 'auto', pb: 1 }}>
                {weekDates.list.map((d) => {
                  const cell = myCellForDate(d);
                  const isToday = d === todayISO;
                  return (
                    <Box
                      key={d}
                      sx={{
                        flex: '0 0 64px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 0.5,
                        p: 1,
                        borderRadius: 'var(--an-radius-sm)',
                        border: `1px solid ${isToday ? 'var(--an-primary)' : 'var(--an-border-light)'}`,
                        backgroundColor: isToday ? 'rgb(var(--an-primary-rgb) / 0.08)' : 'var(--an-surface-muted)',
                      }}
                    >
                      <Typography variant="caption" sx={{ color: 'var(--an-text-secondary)', fontSize: '0.62rem' }}>
                        {formatDateOnly(d, i18n.language || 'en', { weekday: 'short', day: 'numeric' })}
                      </Typography>
                      {cell ? (
                        <Chip
                          label={cell.code || cell.name?.slice(0, 3)}
                          size="small"
                          sx={{ backgroundColor: cell.color || 'var(--an-primary)', color: 'var(--an-white)', fontWeight: 800, fontSize: '0.65rem' }}
                        />
                      ) : (
                        <Typography variant="caption" sx={{ color: 'var(--an-text-tertiary)' }}>—</Typography>
                      )}
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          </Box>
        </Grid>

        {/* Swap Requests */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Box sx={{
            borderRadius: 'var(--an-radius-md)',
            border: '1px solid var(--an-border-light)',
            boxShadow: 'var(--an-shadow-sm)',
            backgroundColor: 'var(--an-surface)',
            p: 3,
            height: '100%',
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" fontWeight={800} sx={{ color: 'var(--an-text-primary)' }}>
                <i className="bi bi-arrow-left-right me-2" />
                {t('employeeDashboard.swapRequests')}
              </Typography>
              <Button
                variant="contained"
                size="small"
                onClick={openSwapModal}
                disabled={actionLoading}
                sx={{ textTransform: 'none', fontWeight: 700 }}
              >
                <i className="bi bi-plus-lg me-1" />
                {t('employeeDashboard.requestSwap')}
              </Button>
            </Box>

            {/* Incoming */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ color: 'var(--an-text-secondary)', fontWeight: 700, mb: 1 }}>
                {t('employeeDashboard.incoming')}
              </Typography>
              {incoming.length === 0 ? (
                <Typography variant="body2" sx={{ color: 'var(--an-text-tertiary)' }}>{t('employeeDashboard.noSwaps')}</Typography>
              ) : (
                <Stack spacing={1}>
                  {incoming.map((sw) => (
                    <Box key={sw.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5, borderRadius: 'var(--an-radius-sm)', border: '1px solid var(--an-border-light)' }}>
                      <Box>
                        <Typography variant="body2" fontWeight={700}>{sw.requester_employee?.name}</Typography>
                        <Typography variant="caption" sx={{ color: 'var(--an-text-secondary)' }}>{formatSwapDate(sw.swap_date)}</Typography>
                      </Box>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip label={t(`employeeDashboard.${sw.status}`)} size="small" color={STATUS_VARIANT[sw.status]} />
                        {sw.status === 'pending' && (
                          <>
                            <Button size="small" variant="contained" color="success" onClick={() => handleRespond(sw.id, 'accept')} disabled={actionLoading}>
                              {t('employeeDashboard.acceptSwap')}
                            </Button>
                            <Button size="small" variant="outlined" color="error" onClick={() => handleRespond(sw.id, 'decline')} disabled={actionLoading}>
                              {t('employeeDashboard.declineSwap')}
                            </Button>
                          </>
                        )}
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>

            {/* Outgoing */}
            <Box>
              <Typography variant="subtitle2" sx={{ color: 'var(--an-text-secondary)', fontWeight: 700, mb: 1 }}>
                {t('employeeDashboard.outgoing')}
              </Typography>
              {outgoing.length === 0 ? (
                <Typography variant="body2" sx={{ color: 'var(--an-text-tertiary)' }}>{t('employeeDashboard.noSwaps')}</Typography>
              ) : (
                <Stack spacing={1}>
                  {outgoing.map((sw) => (
                    <Box key={sw.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5, borderRadius: 'var(--an-radius-sm)', border: '1px solid var(--an-border-light)' }}>
                      <Box>
                        <Typography variant="body2" fontWeight={700}>{t('employeeDashboard.target')}: {sw.target_employee?.name}</Typography>
                        <Typography variant="caption" sx={{ color: 'var(--an-text-secondary)' }}>{formatSwapDate(sw.swap_date)}</Typography>
                      </Box>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip label={t(`employeeDashboard.${sw.status}`)} size="small" color={STATUS_VARIANT[sw.status]} />
                        {sw.status === 'pending' && (
                          <Button size="small" variant="outlined" color="error" onClick={() => setConfirmCancelSwap(sw.id)} disabled={actionLoading}>
                            {t('employeeDashboard.cancelSwap')}
                          </Button>
                        )}
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>
          </Box>
        </Grid>
      </Grid>

      {/* Request swap dialog */}
      <Dialog open={swapOpen} onClose={() => setSwapOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('employeeDashboard.requestSwap')}</DialogTitle>
        <DialogContent>
          {swapError && <Alert severity="error" sx={{ mb: 2 }}>{swapError}</Alert>}
          <TextField
            select
            fullWidth
            label={t('employeeDashboard.selectDate')}
            value={swapDate}
            onChange={(e) => { setSwapDate(e.target.value); setPartnerId(''); }}
            sx={{ mt: 1 }}
          >
            {swapDates.map((d) => (
                <MenuItem key={d} value={d}>
                  {formatSwapDate(d, true)}
                </MenuItem>
              ))}
          </TextField>

          {swapDate && (
            <TextField
              select
              fullWidth
              label={t('employeeDashboard.selectPartner')}
              value={partnerId}
              onChange={(e) => setPartnerId(Number(e.target.value))}
              sx={{ mt: 2 }}
              disabled={candidatesLoading || swapCandidates.length === 0}
              helperText={candidatesLoading
                ? t('employeeDashboard.loadingCandidates')
                : (swapCandidates.length === 0 ? t('employeeDashboard.noCandidates') : undefined)}
            >
              {swapCandidates.map((candidate) => (
                <MenuItem key={candidate.id} value={candidate.id}>
                  {candidate.name} ({candidate.shift?.code || candidate.shift?.name || t('employeeDashboard.noShiftToday')})
                </MenuItem>
              ))}
            </TextField>
          )}

          <TextField
            fullWidth
            multiline
            rows={2}
            label={t('employeeDashboard.swapReason')}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('employeeDashboard.swapReasonPlaceholder')}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSwapOpen(false)} disabled={actionLoading}>
            {tc('buttons.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateSwap}
            disabled={actionLoading || !swapDate || !partnerId}
          >
            {actionLoading ? <CircularProgress size={20} /> : t('employeeDashboard.sendRequest')}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={!!successMessage}
        autoHideDuration={3500}
        onClose={() => setSuccessMessage(null)}
        message={successMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
      <ConfirmDialog
        show={confirmCancelSwap !== null}
        title={t('employeeDashboard.cancelSwap')}
        message={t('employeeDashboard.cancelSwapConfirm')}
        onCancel={() => setConfirmCancelSwap(null)}
        onConfirm={() => {
          const swapId = confirmCancelSwap;
          setConfirmCancelSwap(null);
          if (swapId !== null) handleCancel(swapId);
        }}
      />
    </Box>
  );
};

export default EmployeeShiftPanel;
